import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, ArrowRight, Phone, Star, 
  Users, Lightbulb, Clock, Shield, Palette, Briefcase
} from 'lucide-react';

const services = [
  { icon: Palette, title: 'Office Design', description: 'Modern workspace layouts that boost productivity' },
  { icon: Users, title: 'Meeting Rooms', description: 'Professional spaces for client meetings and collaboration' },
  { icon: Lightbulb, title: 'Creative Zones', description: 'Breakout areas and creative spaces for teams' },
  { icon: Shield, title: 'Executive Cabins', description: 'Premium design for leadership offices' },
];

const benefits = [
  'Increase employee productivity by up to 20%',
  'Create strong brand identity through design',
  'Optimize space utilization',
  'Ergonomic furniture solutions',
  'Sustainable and green design options',
  'Complete project management'
];

const stats = [
  { value: '50+', label: 'Corporate Projects' },
  { value: '2M+', label: 'Sq.ft Designed' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '15+', label: 'Design Awards' },
];

const CorporateInterior = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    projectType: '',
    area: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.phone) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/interior-corporate', {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        phone: formData.phone,
        email: formData.email || null,
        project_type: formData.projectType || null,
        area_sqft: formData.area ? parseInt(formData.area) : null,
        message: formData.message || null
      });
      
      toast.success(`Request submitted! Your Request ID: ${response.data.request_id}`);
      
      const whatsappMsg = `Hi ZenStudios! I need corporate interior design services. My Request ID: ${response.data.request_id}. Company: ${formData.companyName}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="corporate-interior-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" /> Corporate Interiors
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Transform Your Workspace Into a Productivity Hub
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Create inspiring office spaces that reflect your brand, boost employee morale, 
                and leave lasting impressions on clients. From startups to enterprises, we 
                design workspaces that work.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20corporate%20interior%20design%20services.', '_blank')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600" 
                alt="Modern Corporate Office"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-primary/20 border-2 border-white" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold">50+ Projects</p>
                    <p className="text-xs text-gray-500">Completed Successfully</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-white/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Corporate Design Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive interior design solutions tailored for modern businesses
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Invest in Professional Office Design?</h2>
              <p className="text-gray-600 mb-8">
                A well-designed workspace is an investment in your company&apos;s future. Studies show 
                that thoughtful office design directly impacts employee satisfaction, retention, 
                and productivity.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=300" 
                alt="Modern Office"
                className="rounded-xl"
              />
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=300" 
                alt="Team Collaboration"
                className="rounded-xl mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20" id="contact-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Start Your Corporate Transformation</h2>
            <p className="text-gray-600">
              Share your requirements and our design experts will get in touch
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    placeholder="Your company name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                    placeholder="Your name"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="company@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Project Type</Label>
                  <Input
                    value={formData.projectType}
                    onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                    placeholder="New office / Renovation"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Approximate Area (sq.ft)</Label>
                  <Input
                    type="number"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    placeholder="e.g., 5000"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Project Details</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your vision, requirements, and timeline..."
                  rows={4}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? 'Submitting...' : 'Request Free Consultation'}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Our design team will contact you within 24 hours to schedule a site visit.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateInterior;
